import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Tooltip,
  IconButton,
  LinearProgress,
  Collapse
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { format } from 'date-fns';

interface Document {
  _id: string;
  title: string;
  content: string;
  required: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

interface DocumentConfirmation {
  documentId: string;
  confirmed: boolean;
  confirmedAt?: string;
}

interface CounsellorDocuments {
  _id: string;
  counsellorId: string;
  confirmations: DocumentConfirmation[];
}

interface DocumentStatusManagementProps {
  counsellorId: string;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const DocumentStatusManagement: React.FC<DocumentStatusManagementProps> = ({
  counsellorId,
  onSuccess,
  onError
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [counsellorDocuments, setCounsellorDocuments] = useState<CounsellorDocuments | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all documents
      const documentsResponse = await api.get('/api/documents');
      setDocuments(documentsResponse.data);
      
      // Fetch counsellor's document confirmations
      const counsellorDocumentsResponse = await api.get(`/api/counsellors/${counsellorId}/documents`);
      setCounsellorDocuments(counsellorDocumentsResponse.data);
      
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch documents';
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [counsellorId, onError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleResetConfirmation = async (documentId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      await api.delete(`/api/counsellor-documents/${counsellorId}/documents/${documentId}/reset`);
      
      const successMessage = 'Document confirmation reset successfully';
      setSuccess(successMessage);
      if (onSuccess) onSuccess(successMessage);
      
      // Refresh data
      await fetchData();
      
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to reset document confirmation';
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isDocumentConfirmed = (documentId: string): boolean => {
    if (!counsellorDocuments) return false;
    
    const confirmation = counsellorDocuments.confirmations.find(
      c => c.documentId === documentId
    );
    
    return confirmation ? confirmation.confirmed : false;
  };

  const getConfirmationDate = (documentId: string): string | undefined => {
    if (!counsellorDocuments) return undefined;
    
    const confirmation = counsellorDocuments.confirmations.find(
      c => c.documentId === documentId
    );
    
    return confirmation?.confirmedAt;
  };

  const getRequiredDocumentsCount = (): number => {
    return documents.filter(doc => doc.required).length;
  };

  const getConfirmedRequiredDocumentsCount = (): number => {
    if (!counsellorDocuments) return 0;
    
    return documents.filter(doc => {
      if (!doc.required) return false;
      
      const confirmation = counsellorDocuments.confirmations.find(
        c => c.documentId === doc._id
      );
      
      return confirmation ? confirmation.confirmed : false;
    }).length;
  };

  const allRequiredDocumentsConfirmed = (): boolean => {
    return getConfirmedRequiredDocumentsCount() === getRequiredDocumentsCount();
  };

  if (loading && !documents.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const requiredDocumentsCount = getRequiredDocumentsCount();
  const confirmedRequiredDocumentsCount = getConfirmedRequiredDocumentsCount();
  const completionPercentage = requiredDocumentsCount > 0 
    ? Math.round((confirmedRequiredDocumentsCount / requiredDocumentsCount) * 100) 
    : 0;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      <Box 
        sx={{ 
          mb: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          cursor: 'pointer',
          p: 2,
          borderRadius: 2,
          bgcolor: 'rgba(255, 255, 255, 0.05)',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.08)'
          }
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Document Confirmations
          </Typography>
          <Chip 
            label={`${confirmedRequiredDocumentsCount} of ${requiredDocumentsCount} confirmed`}
            color={allRequiredDocumentsConfirmed() ? 'success' : 'warning'}
            size="small"
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={(e) => {
              e.stopPropagation();
              fetchData();
            }} 
            size="small" 
            title="Refresh" 
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            <RefreshIcon />
          </IconButton>
          <IconButton size="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>
      
      <Collapse in={expanded}>
        <Box sx={{ mb: 3, mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              {confirmedRequiredDocumentsCount} of {requiredDocumentsCount} required documents confirmed
            </Typography>
            <Typography variant="body2" fontWeight="bold" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              {completionPercentage}%
            </Typography>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={completionPercentage} 
            sx={{ height: 8, borderRadius: 4 }}
          />
          
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Chip 
              label={allRequiredDocumentsConfirmed() ? "All Required Documents Confirmed" : "Incomplete"} 
              color={allRequiredDocumentsConfirmed() ? "success" : "warning"}
              size="small"
            />
          </Box>
        </Box>
        
        <List sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2 }}>
        {documents.sort((a, b) => a.order - b.order).map((document) => {
          const isConfirmed = isDocumentConfirmed(document._id);
          const confirmationDate = getConfirmationDate(document._id);
          
          return (
            <React.Fragment key={document._id}>
              <ListItem
                secondaryAction={
                  isConfirmed && (
                    <Tooltip title="Reset confirmation (requires counsellor to confirm again)">
                      <IconButton 
                        edge="end" 
                        aria-label="reset" 
                        onClick={() => handleResetConfirmation(document._id)}
                      >
                        <RefreshIcon color="action" />
                      </IconButton>
                    </Tooltip>
                  )
                }
              >
                <ListItemIcon>
                  <DescriptionIcon color={document.required ? "primary" : "action"} />
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {document.title}
                      </Typography>
                      {document.required && (
                        <Tooltip title="Required Document">
                          <InfoIcon color="error" fontSize="small" sx={{ ml: 1 }} />
                        </Tooltip>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      {isConfirmed ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Confirmed {confirmationDate && `on ${format(new Date(confirmationDate), 'dd MMM yyyy')}`}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CancelIcon color="error" fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Not confirmed
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItem>
              <Divider variant="inset" component="li" />
            </React.Fragment>
          );
        })}
        </List>
      </Collapse>
    </Box>
  );
};

export default DocumentStatusManagement;
